import { cn } from '@/lib/cn';
import { cva, VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import Tooltip from 'components/tooltip/Tooltip';
import { ButtonHTMLAttributes, forwardRef, MouseEvent, ReactNode } from 'react';

// TODO: Make sure to update any Button skeletons and h-button when the button size changes. i.e. Dashboard.tsx
export const buttonVariants = cva(
    'inline-flex items-center select-none focus:ring-0 focus:outline-none disabled:pointer-events-none aria-disabled:pointer-events-none',
    {
        variants: {
            variant: {
                destructive:
                    'py-input px-md font-semibold rounded leading-input bg-destructiveButtonBackground text-primaryButtonText whitespace-nowrap hover:bg-destructiveButtonBackgroundHover data-[state="open"]:bg-destructiveButtonBackgroundHover focus:bg-destructiveButtonBackgroundHover disabled:bg-destructiveButtonBackgroundDisabled disabled:text-primaryButtonTextDisabled',
                primary:
                    'py-input px-md font-semibold rounded leading-input bg-primaryButtonBackground text-primaryButtonText whitespace-nowrap hover:bg-primaryButtonBackgroundHover data-[state="open"]:bg-primaryButtonBackgroundHover focus:bg-primaryButtonBackgroundHover disabled:bg-primaryButtonBackgroundDisabled disabled:text-primaryButtonTextDisabled',
                secondary:
                    'py-inputWithBorder px-md font-semibold rounded ring-0 focus:ring-0 leading-input bg-secondaryButtonBackground text-secondaryButtonText whitespace-nowrap border border-secondaryButtonOutline hover:bg-secondaryButtonBackgroundHover hover:border-secondaryButtonOutlineHover data-[state="open"]:bg-secondaryButtonBackgroundHover data-[state="open"]:border-secondaryButtonOutlineHover focus:bg-secondaryButtonBackgroundHover focus:border-secondaryButtonOutlineHover disabled:bg-secondaryButtonBackgroundDisabled disabled:text-secondaryButtonTextDisabled',
                tertiary:
                    'text-tertiaryButton tracking-body hover:text-tertiaryButtonHover focus-visible:text-tertiaryButtonHover whitespace-nowrap disabled:text-tertiaryButtonDisabled',
                link: 'text-textLink tracking-body hover:underline focus-visible:underline disabled:text-textLinkDisabled',
                choice: 'py-input px-md rounded leading-input bg-componentBackgroundPrimary text-textPrimary whitespace-nowrap ring-tileOutline ring-inset ring-1 hover:bg-secondaryButtonBackgroundHover hover:ring-secondaryButtonOutlineHover focus-visible:secondaryButtonBackgroundHover focus:ring-1 focus-visible:ring-secondaryButtonOutlineHover disabled:bg-componentBackgroundPrimary disabled:text-textDisabled disabled:ring-tileOutline aria-disabled:bg-componentBackgroundPrimary aria-disabled:text-textDisabled aria-disabled:ring-tileOutline',
                upgrade:
                    'py-input px-md font-semibold rounded leading-input bg-upgradeButtonBackgroundPrimary text-upgradeButtonTextPrimary whitespace-nowrap hover:bg-upgradeButtonBackgroundHover focus-visible:bg-upgradeButtonBackgroundHover disabled:bg-upgradeButtonBackgroundDisabled disabled:text-upgradeButtonTextDisabled'
            }
        },
        defaultVariants: {
            variant: 'primary'
        }
    }
);

type ClickFn = (() => void) | ((e: MouseEvent) => void);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    icon?: ReactNode;
    href?: string;
    onClick?: ClickFn;
    dataTestId?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ href, title, icon, variant, className, children, type, disabled, onClick, dataTestId, ...other }, ref) => {
        let buttonType = type;

        if (buttonType == null) {
            buttonType = onClick ? 'button' : 'submit';
        }

        const clickHandler = (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (onClick) {
                onClick(e);
            }

            // Blur in case this button remains visible and nothing else
            // takes focus, which without this leaves the button with it's
            // focused styles until the user clicks elsewhere
            e.currentTarget?.blur();

            if (href) {
                // Deny the opened page a handle on this window. Not noreferrer: our sites read the referrer
                window.open(href, '_blank', 'noopener');
            }
        };

        const button = (
            <button
                ref={ref}
                aria-label={title}
                onClick={clickHandler}
                type={buttonType}
                className={cn([buttonVariants({ variant }), className])}
                disabled={disabled}
                data-testid={dataTestId}
                {...other}
            >
                {icon && <span className={clsx(children && 'mr-3 inline-flex items-center')}>{icon}</span>}
                {children}
            </button>
        );

        if (title) {
            return (
                <Tooltip title={title} disabled={disabled}>
                    {button}
                </Tooltip>
            );
        }

        return button;
    }
);

export default Button;
